import { objectSpread2 as _objectSpread2 } from '../_virtual/_rollupPluginBabelHelpers.js';
import PropTypes from 'prop-types';
import React__default from 'react';
import { states, CC_WINDOW, CHATBOT_INITIALIZE, ZENDESK_INITIALIZE, CC_VISIBLE, SHOW_WIDGET, ZENDESK_UPDATE_VISIBILITY, ZENDESK_UPDATE_UNREAD_COUNT, ZENDESK_UPDATE_DEPARTMENTS, ZENDESK_UPDATE_LANGUAGE, ZENDESK_UPDATE_TAGS, ZENDESK_UPDATE_NOTIFICATIONS, ZENDESK_UPDATE_IDENTITY, visibility, UPDATE_AUTHKEY, ZENDESK_LOAD, ZE_SNIPPET, CHATBOT_LOAD, CC_SNIPPET, ZENDESK_SET_LAUNCHER_ELIGIBLE } from './constants.js';
import { usePolling } from './utils.js';
import { useChatReducer } from './reducer.js';

/* global zE, $zopim */

var ChatContext = /*#__PURE__*/React__default.createContext({});
/**
 * Context Provider for Support Chat
 *
 * It is the Provider's responsibility to keep track of the state,
 * and to provide certain functions that can be used by the consumer to update
 * the state.
 *
 * It is the Consumer's repsonsibility to memoize props where necessary to
 * prevent unnecessary updates.
 *
 * @param {object} props
 * @param {string} props.appKey Necessary for loading the widget script.
 * @param {string} props.chatBotId Necessary for loading the chatbot if it will be used.
 * @param {color} props.color Sets color of zendesk launcher. Must be in Hex
 * format (#B00B1E) Will default to color defined in widget setup in Zendesk
 * back-office and is in most cases not needed.
 * @param {number} props.departmentId Primary department id, found in host app config.
 * @param {boolean} props.disabled Forces all chat elements to be hidden.
 * It is intended to be kept as a static prop to override props.hidden which
 * can be toggled. If this prop is truthy, no chat elements will be displayed.
 * @param {boolean} props.disableMobileNotifications Disables the zendesk mobile
 * notifications. (Think Push-notifications that looks like banners)
 * @param {string} props.email Players email to be updated in zendesk settings.
 * @param {number} props.fallbackDepartmentId Fallback department id, found in
 * host app config.
 * @param {boolean} props.forceOpenOnUnreadMsgs Setting to force open the widget
 * whenever user gets a new message.
 * @param {boolean} props.hidden Forces all chat elements to be hidden by default.
 * Hides the launcher the same way as `disabled` does, but allows for the widget
 * to still be toggled open with the context method `setWidgetVisibility`
 * @param {string} props.language The language code from the host app locale.
 * @param {string} props.name The players name.
 * @param {number} props.showZeLauncherFrom FKA 'visibleAt'. The screen-width in
 * pixels from which the zendesk launcher should be visible. (default 800px)
 * @param {Array.<string>} props.tags The tags requested by the customer support team
 *
 * @example // Basic usage with React Helmet
 * // Herz does not manage <head>; <script> tag injection is up to consumer.
 * // The widget script can be loaded in the head or body of the app, so this is
 * // left up to the consumer to decide also. If the ChatCreate script is used,
 * // this must be immediately AFTER the zendesk widget script.
 *
 * export function ChatProvider(props){
 *  return (
 *    <Herz.Chat.ChatProvider
 *      hidden
 *      forceOpenOnUnreadMsgs={Boolean(failedFirstDepositTag)}
 *       appKey={liveChatKey}
 *       departmentId={department.primary}
 *       fallbackDepartmentId={department.fallback}
 *       language={language}
 *       email={email}
 *       name={displayName}
 *       tags={R.filter((item) => !R.isNil(item), [birthdateTag, failedFirstDepositTag, valueSegmentTag])}
 *       translations={translations}
 *     >
 *       {props.children}
 *     </Herz.Chat.ChatProvider>
 *  )
 * }
 *
 * export function Consumer() {
 *  const context = Herz.Chat.useContext()
 *   return (
 *    <Helmet>
 *      <script type="text/javascript">
 *       {`window.zESettings = ${JSON.stringify(context.initialSettings)}`}
 *      </script>
 *      <script {...context.zendeskScript()} />
 *      <script {...context.chatCreateScript()} />
 *    </Helmet>
 *   )
 *  }
 *
 *
 * @returns ChatProvider Provider Component
 */

function ChatProvider(props) {
  var [state, dispatch] = useChatReducer(props);
  /**
   * useChatCreateInitialize
   *
   * This Hook checks for availability of the Chatbot DOM element on the window
   * object, and updates local state if so.
   */

  var chatCreateDeps = [state.chatbot.status];
  usePolling(() => {
    // Make sure chatbot DOM element is added on document
    var chatBot = document.getElementById(CC_WINDOW);
    dispatch({
      type: CHATBOT_INITIALIZE,
      payload: chatBot
    });
  }, {
    deps: chatCreateDeps,
    condition: state.chatbot.status === states.LOADING
  });
  /**
   * useZendeskInitiated
   *
   * This hook checks availability of zE api on window and updates state when it
   * was initialized.
   *
   * If chatbot is enabled on the brand, we must first wait for that to be present
   * before checking for the zendesk widget availability.
   * More information here: https://github.com/RushPlay/support-chat/pull/38
   */

  React__default.useEffect(() => {
    if (state.zendesk.status === states.LOADING) {
      var timerId = window.setInterval(() => {
        var initialized = typeof zE !== "undefined" && typeof $zopim !== "undefined";
        dispatch({
          type: ZENDESK_INITIALIZE,
          payload: initialized
        });
      }, 250);
      return () => {
        clearInterval(timerId);
      };
    }
  }, [state.zendesk.status]);
  /**
   * useToggleChatOnChatbotClose
   *
   * This hook syncs the state to hide chat features when user closes the chatbot.
   */

  React__default.useEffect(() => {
    if (state.chatbot.element !== null) {
      var observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === "class" && !state.chatbot.element.classList.contains(CC_VISIBLE) && state.chatbot.element.style.display !== "none") {
            return dispatch({
              type: SHOW_WIDGET,
              payload: false
            });
          }
        });
      });
      observer.observe(state.chatbot.element, {
        attributes: true
      });
      return () => {
        observer.disconnect();
      };
    }
  }, [state.chatbot.element]);
  /**
   * useCheckScreenWidth
   *
   * This hook manages listener for media-query that updates state when matching
   * props.showZeLauncherFrom
   *
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    }

    function updateEligibility(e) {
      dispatch({
        type: ZENDESK_SET_LAUNCHER_ELIGIBLE,
        payload: e.matches
      });
    }

    var mq = window.matchMedia("(min-width: ".concat(props.showZeLauncherFrom, "px)"));
    updateEligibility(mq);
    mq.addListener(updateEligibility);
    return () => {
      mq.removeListener(updateEligibility);
    };
  }, [state.zendesk.status, props.showZeLauncherFrom]);
  /**
   * useHandleVisibility
   *
   * This hook manages the visibility of both chat features when chatbot becomes
   * active, since zendesk should be hidden when the chatbot is active.
   *
   */

  React__default.useEffect(() => {
    // "Chat Create" sets the window display to 'none' when it hands over
    // conversation to Zendesk
    if (state.showWidget && state.chatbot.element && state.chatbot.element.style.display !== "none") {
      window.ccShowWidget("600px");
    }

    dispatch({
      type: ZENDESK_UPDATE_VISIBILITY
    });
  }, [state.chatbot.element, state.showWidget]);
  /**
   * useOnWidgetClose
   *
   * Keeps state informed that chat feature is hidden when widget is closed.
   * [webWidget:on close]{@link https://developer.zendesk.com/embeddables/docs/widget/core#on-close}
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    }
    /**
     * Executes a callback when the widget is closed.
     * [webWidget:on close]{@link https://developer.zendesk.com/embeddables/docs/widget/core#on-close}
     */


    zE("webWidget:on", "close", () => {
      dispatch({
        type: SHOW_WIDGET,
        payload: false
      });
    });
  }, [props.showZeLauncherFrom, state.zendesk.status]);
  /**
   * useHandleUnreadMessages
   *
   * Handles unreadmessages and forceOpenOnUnreadMsgs
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    }
    /**
     * Registers a callback to be fired when the number of unread messages changes.
     * [webWidget:on chat:unreadMessages]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#on-chatunreadmessages}
     */


    zE("webWidget:on", "chat:unreadMessages", count => {
      // Update redux with count for use wherever in host-app
      dispatch({
        type: ZENDESK_UPDATE_UNREAD_COUNT,
        payload: count
      }); // Force open chat on unread msgs

      if (props.forceOpenOnUnreadMsgs) {
        dispatch({
          type: SHOW_WIDGET,
          payload: true
        });
      }
    });
    /**
     * Executes a callback when the widget is opened.
     * [webWidget:on open]{@link https://developer.zendesk.com/embeddables/docs/widget/core#on-open}
     */

    zE("webWidget:on", "open", () => {
      // We need to mark the messages as read ourselves and the callback above
      // is not triggered when they're read.
      dispatch({
        type: ZENDESK_UPDATE_UNREAD_COUNT,
        payload: 0
      });
      /**
       * Trigger check for visibility.
       * Synchronises state when chatbot hands over to zendesk.
       */

      dispatch({
        type: ZENDESK_UPDATE_VISIBILITY
      });
    });
  }, [props.forceOpenOnUnreadMsgs, state.zendesk.status]);
  /**
   * useUpdateDepartments
   *
   * This hook handles funneling user into the correct departments
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    }

    if (props.departmentId || props.fallbackDepartmentId) {
      zE(() => {
        /**
         * Registers a callback to be fired when the widget successfully connects to the server.
         * [webWidget:on chat:connected]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#on-chatconnected}
         */
        zE("webWidget:on", "chat:connected", () => {
          /**
           * Gets the data of a specific department.
           * We care about the status so we fetch the departments.
           * [webWidget:get chat:department]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#get-chatdepartment}
           * @returns {DepartmentObject}
           */
          var primaryDepartment = zE("webWidget:get", "chat:department", props.departmentId);
          var fallbackDepartment = zE("webWidget:get", "chat:department", props.fallbackDepartmentId);
          dispatch({
            type: ZENDESK_UPDATE_DEPARTMENTS,
            payload: {
              primary: primaryDepartment,
              fallback: fallbackDepartment
            }
          });
          /**
           * Registers a callback to be fired when a department status changes.
           * [webWidget:on chat:departmentStatus]{@link https://developer.zendesk.com/embeddables/docs/widget/chat#on-chatdepartmentstatus}
           * returns the department object containing the updated status.
           */

          zE("webWidget:on", "chat:departmentStatus", dep => {
            dispatch({
              type: ZENDESK_UPDATE_DEPARTMENTS,
              payload: {
                primary: dep.id === props.departmentId && dep,
                fallback: dep.id === props.fallbackDepartmentId && dep
              }
            });
          });
        });
      });
    }
  }, [props.departmentId, props.fallbackDepartmentId, state.zendesk.status]);
  /**
   * useUpdateLanguage
   * Hook to update state when props.language changes
   */

  React__default.useEffect(() => {
    if (props.language) {
      dispatch({
        type: ZENDESK_UPDATE_LANGUAGE,
        payload: props.language
      });
    }
  }, [props.language]);
  /**
   * useUpdateTags
   * This hooks handles updating of the tags that the CS-agent will see
   */

  React__default.useEffect(() => {
    var _props$tags;

    /**
     * If you log out tags should ideally be cleared in the host-app
     * But we want to keep the tags to faster help the customer
     * I.E. Only update if tags is not empty
     */
    if ((_props$tags = props.tags) !== null && _props$tags !== void 0 && _props$tags.length) {
      dispatch({
        type: ZENDESK_UPDATE_TAGS,
        payload: props.tags
      });
    }
  }, [props.tags]);
  /**
   * useUpdateMobileNotification
   * Hook to handle updating mobile notification settings when props change
   */

  React__default.useEffect(() => {
    dispatch({
      type: ZENDESK_UPDATE_NOTIFICATIONS,
      payload: {
        disable: props.forceOpenOnUnreadMsgs || props.disableMobileNotifications
      }
    });
  }, [props.forceOpenOnUnreadMsgs, props.disableMobileNotifications]);
  /**
   * useUpdateIdentity
   * Hook to update state with name and email values when they change in props.
   */

  React__default.useEffect(() => {
    if (props.email || props.name) {
      dispatch({
        type: ZENDESK_UPDATE_IDENTITY,
        payload: {
          name: props.name,
          email: props.email
        }
      });
    }
  }, [props.email, props.name]);
  /**
   * Handles call to zendesk zE("webWidget", ("hide"|"show"|"close"|"open")) for
   * setting visibility. Calls to zendesk for visibility should only be made
   * here.
   *
   * The visibility state should be updated internally by dispatching
   * `type: Constants.ZENDESK_UPDATE_VISIBILITY` which will trigger a comparison
   * of state values. Any changes to the visibility in state will ultimately be
   * picked up here.
   *
   *
   * "Middleware" for zE api call
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    }

    switch (state.zendesk.visibility) {
      case visibility.HIDE:
        zE(() => {
          /**
           * Hides widget/launcher completely
           * [webWidget hide]{@link https://developer.zendesk.com/embeddables/docs/widget/core#hide}
           */
          zE("webWidget", "hide");
        });
        break;

      case visibility.LAUNCHER:
        {
          zE(() => {
            /**
             * If the widget was hidden we must first show it before we can un-collapse it
             * [webWidget show]{@link https://developer.zendesk.com/embeddables/docs/widget/core#show}
             */
            zE("webWidget", "show");
            /**
             * Forces the widget to close/collapse, but the launcher is still shown.
             * [webWidget close]{@link https://developer.zendesk.com/embeddables/docs/widget/core#close}
             */

            zE("webWidget", "close");
          });
          break;
        }

      case visibility.WIDGET:
        {
          zE(() => {
            /**
             * If the widget was hidden we must first show it before we can un-collapse it
             * [webWidget show]{@link https://developer.zendesk.com/embeddables/docs/widget/core#show}
             */
            zE("webWidget", "show");
            /**
             * Forces the widget to open/un-collapse.
             * [webWidget open]{@link https://developer.zendesk.com/embeddables/docs/widget/core#open}
             */

            zE("webWidget", "open");
          });
          break;
        }
    }
  }, [state.zendesk.visibility, state.zendesk.status]);
  /**
   * Manage calls to zE('webWidget', 'setLocale', data<string>)
   *
   * Sets the widget locale.
   * [webwidget setLocale]{@link https://developer.zendesk.com/api-reference/widget/core/#setlocale}
   *
   * "Middleware" for zE api call
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    }

    zE(() => {
      zE("webWidget", "setLocale", state.zEcommands.setLocale);
    });
  }, [state.zEcommands.setLocale, state.zendesk.status]);
  /**
   * Manage calls to zE('webWidget', 'identify', data<object>)
   *
   * Identifies an end user to Zendesk
   * [webwidget identify]{@link https://developer.zendesk.com/embeddables/docs/widget/core#identify}
   *
   * "Middleware" for zE api call
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    } // if name gets prefilled as undefined, the name input field won't update
    // the value for some reason, so this is to avoid updating the name field
    // when none exists.


    var values = {};

    for (var field in state.zEcommands.identify) {
      var value = state.zEcommands.identify[field];

      if (value) {
        values[field] = value;
      }
    }

    if (Object.keys(values).length === 0) {
      return;
    }

    zE(() => {
      zE("webWidget", "identify", values);
    });
  }, [state.zEcommands.identify, state.zendesk.status]);
  /**
   * Manage calls to zE('webWidget', 'prefill', data<object>)
   *
   * Prefills the offline-form with customer data if chat is offline.
   * We set it as readOnly as we're appending internal username to name for
   * faster handling and we don't want them to be able to edit it.
   * [webwidget prefill]{@link https://developer.zendesk.com/embeddables/docs/widget/core#prefill}
   *
   * "Middleware" for zE api call
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    } // if name gets prefilled as undefined, the name input field won't update
    // the value for some reason, so this is to avoid updating the name field
    // when none exists.


    var values = {};

    for (var field in state.zEcommands.prefill) {
      var value = state.zEcommands.prefill[field];

      if (value.value) {
        values[field] = value;
      }
    }

    if (Object.keys(values).length === 0) {
      return;
    }

    zE(() => {
      zE("webWidget", "prefill", values);
    });
  }, [state.zEcommands.prefill, state.zendesk.status]);
  /**
   * Manage calls to zE('webWidget', 'updateSettings', data<object>)
   *
   * [webWidget updateSettings]{@link https://developer.zendesk.com/embeddables/docs/widget/core#updatesettings}
   *
   * "Middleware" for zE api call
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    }

    zE(() => {
      zE("webWidget", "updateSettings", _objectSpread2({}, state.zEsettings));
    });
  }, [state.zEsettings, state.zendesk.status]);
  /**
   * Manage calls to $zopim.livechat.setStatus(state.status)
   *
   * zE doesn't have a way of setting status of webwidget yet.
   * According to the migration-guide the old way is however still supported.
   * [Migration Guide - setStatus]{@link https://developer.zendesk.com/embeddables/docs/widget/chat_api_migration#customization-apis}
   * [$zopim.livechat.setStatus]{@link https://api.zopim.com/files/meshim/widget/controllers/LiveChatAPI-js.html}
   *
   * "Middleware" for zE api call
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    }

    zE(() => {
      $zopim(() => {
        /* istanbul ignore next: test is positive on wrapped function call */
        $zopim.livechat.setStatus(state.chatStatus);
      });
    });
  }, [state.chatStatus, state.zendesk.status]);
  /**
   * Manage calls to zE("webWidget", "chat:reauthenticate")
   *
   * This hook handles triggering of chat reauthentication
   *
   * "Middleware" for zE api call
   */

  React__default.useEffect(() => {
    if (state.zendesk.status !== states.INITIALIZED) {
      return;
    }

    if (state.authKey) {
      zE(() => {
        /**
         * Reauthenticates a user during a session. Useful when user is logging in from
         * a logged out state and the page isn't reloaded.
         *
         * NOTE: Authentication only works properly if there's a jwtFn supplied to the widget.
         *
         * "HACK": Coincidentally triggers, setup in Zendesk Backoffice, that rely on the widgets initial
         * load are fired again. Useful if you want to trigger pro-active chats based upon run-time
         * changes that isn't possible otherwise.
         * [webWidget chat:reauthenticate] {@link https://developer.zendesk.com/api-reference/widget/chat-api/#chatreauthenticate}
         */
        zE("webWidget", "chat:reauthenticate");
      });
    }
  }, [state.authKey, state.zendesk.status]);
  var contextValue = React__default.useMemo(() => ({
    initialSettings: state.zEsettings,
    showWidget: state.showWidget,
    unreadCount: state.unreadCount,

    /**
     * Update to be dispatched if you want to reauthenticate the widget
     * against the server. Doing so will cause triggers setup in Zendesks
     * backoffice that rely on widgets inital load to trigger.
     * @type {function(): void}
     */
    reauthenticateWidget: () => dispatch({
      type: UPDATE_AUTHKEY,
      // Date.now used instead of random string generator because uuid and
      // nanoid modules were causing issues with the build. This solution
      // should be sufficient enough to trigger an update.
      payload: Date.now()
    }),

    /**
     * Update to be dispatched if you want to manually force the widget
     * to an open or closed state. Applies to both the chatbot and zendesk widgets.
     * @type {function(bool): void}
     */
    setWidgetVisibility: bool => dispatch({
      type: SHOW_WIDGET,
      payload: bool
    }),

    /**
     * Update state and trigger checking for script initialization.
     * This delays checking for initialization until the script has been added,
     * which in some cases is only on certain routes.
     * @returns {object} widget script attributes
     */
    zendeskScript: () => {
      if (!props.appKey) {
        /* istanbul ignore next: difficult to mock for test. TODO later */
        throw new Error("appKey prop missing from Chat.Provider");
      }

      dispatch({
        type: ZENDESK_LOAD
      });
      return {
        type: "text/javascript",
        id: ZE_SNIPPET,
        src: "https://static.zdassets.com/ekr/snippet.js?key=".concat(props.appKey)
      };
    },

    /**
     * Update state and trigger checking for script initialization. This delays
     * checking for initialization until the script has been added, which in
     * some cases is only on certain routes.
     * @returns {object} chatcreate script attributes
     */
    chatCreateScript: () => {
      if (props.chatBotId) {
        dispatch({
          type: CHATBOT_LOAD
        });
        return {
          type: "text/javascript",
          id: CC_SNIPPET,
          src: "https://chatcreate.com/api/bots/".concat(props.chatBotId, "/zendesk/snippet")
        };
      }
    }
  }), [state.showWidget, state.unreadCount, state.zEsettings, props.appKey, props.chatBotId]);
  return /*#__PURE__*/React__default.createElement(ChatContext.Provider, {
    value: contextValue
  }, props.children);
}
ChatProvider.displayName = "Chat.Provider";
ChatProvider.defaultProps = {
  showZeLauncherFrom: 800,
  addScriptInHead: false
};
ChatProvider.propTypes = {
  appKey: PropTypes.string.isRequired,
  chatBotId: PropTypes.string,
  color: PropTypes.string,
  children: PropTypes.node,
  departmentId: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  disableMobileNotifications: PropTypes.bool,
  email: PropTypes.string,
  fallbackDepartmentId: PropTypes.number,
  forceOpenOnUnreadMsgs: PropTypes.bool,
  hidden: PropTypes.bool,
  language: PropTypes.string,
  name: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
  showZeLauncherFrom: PropTypes.number,
  translations: PropTypes.shape({
    "chat.concierge.avatarPath": PropTypes.string,
    "chat.concierge.name": PropTypes.string,
    "chat.concierge.title": PropTypes.string,
    "chat.offlineForm.greeting": PropTypes.string,
    "chat.title": PropTypes.string,
    "launcher.chatLabel": PropTypes.string,
    "launcher.label": PropTypes.string
  }).isRequired
};
function useContext() {
  return React__default.useContext(ChatContext);
}

export { ChatProvider, useContext };
